import { holdAutomaticChunkReload } from "../lib/chunkReload.js";
// Findings review with explicit access to completed page-session runs.
//
// The calling surface supplies `loadInput`, an adapter that does the
// authorized reads for its own scope and returns { selection, findings,
// attribution, collectedAt, notes }. The panel never reads ambient page state
// to decide membership. History retains the original input for source links;
// closing the page discards both history and the runner result cache.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { getReviewHistory, subscribeReviewHistory, rememberReview } from "../lib/findingsReviewHistory.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import Select from "./Select.jsx";
import { getChatCreds } from "../lib/chatCreds.js";
import { getRankedModels } from "../lib/modelCatalog.js";
import { defaultReviewModel } from "../lib/traceReview.js";
import { TEMPLATES, TEMPLATE_IDS } from "../lib/findingsReviewPrompts.js";
import { DEFAULT_MAX_PACKET_BYTES } from "../lib/findingsReviewPacket.js";
import { buildGapChatSeed, gapIsAnswerable } from "../lib/findingsReviewChatSeed.js";
import { DOCKET_FOCUS, DOCKET_TRANSITION, DOCKET_NOTE_CLASS, SECTION_EYEBROW_CLASS } from "../lib/docketUi.js";

// The runner drags the chat runtime into the module graph, so it is loaded
// on first use rather than when the page mounts.
async function defaultRunReview(args) {
    const { runFindingsReview } = await import("../lib/findingsReview.js");
    return runFindingsReview(args);
}

const isModuleLoadError = (message) => /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed/i.test(message || "");

const PROSE = "mt-[4px] break-words text-[12.5px] leading-[1.5] text-[color:var(--muted)]";
const INK = "text-[12.5px] leading-[1.5] text-[color:var(--ink)]";
const CHIP = `${DOCKET_FOCUS} ${DOCKET_TRANSITION} inline-flex cursor-pointer items-center rounded-[2px] border border-[color:var(--line-control)] bg-transparent px-[7px] py-[2px] text-[11px] leading-[1.3] text-[color:var(--ink)] hover:border-[color:var(--ink)]`;
const BUTTON = `${DOCKET_FOCUS} ${DOCKET_TRANSITION} inline-flex h-[24px] cursor-pointer items-center rounded-[2px] border border-[color:var(--line-control)] bg-[color:var(--bg)] px-[9px] text-[11px] leading-none text-[color:var(--ink)] hover:border-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-50`;
const SELECT_STYLE = { width: "auto", maxWidth: "100%", height: 24, minHeight: 0, fontSize: 10, backgroundColor: "var(--bg)", backgroundImage: "linear-gradient(var(--control-bg),var(--control-bg))" };

const ATTENTION_LABEL = { now: "Needs attention now", soon: "Needs attention soon", monitor: "Monitor", none: "No action needed" };
const CONCLUSION_LABEL = {
    supported: "The evidence supports a security concern",
    partially_supported: "The evidence partly supports a security concern",
    not_supported: "The evidence does not support a security concern",
    unresolved: "The evidence cannot decide whether there is a security concern",
};
const kb = (bytes) => `${Math.max(1, Math.round(bytes / 1000))} KB`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Long alias lists collapse behind a count. The model sometimes cites every
// finding in a comparison, and fifty chips say less than "52 findings".
const CHIP_PREVIEW = 12;

function AliasChips({ aliases, lookup, label, onOpen, render }) {
    const [expanded, setExpanded] = useState(false);
    if (!aliases.length) return <span className={DOCKET_NOTE_CLASS}>{label}: none</span>;
    const shown = expanded ? aliases : aliases.slice(0, CHIP_PREVIEW);
    const hidden = aliases.length - shown.length;
    return (
        <div className="mt-[4px] flex flex-wrap items-center gap-[4px]">
            <span className={DOCKET_NOTE_CLASS}>{label} ({aliases.length}):</span>
            {shown.map((alias) => {
                const item = lookup.get(alias);
                const text = render(item);
                return onOpen && item ? (
                    <button key={alias} type="button" className={CHIP} title={text} onClick={() => onOpen(item)}>{alias}</button>
                ) : <span key={alias} className={`${CHIP} cursor-default`} title={text}>{alias}</span>;
            })}
            {hidden > 0 ? <button type="button" className={`${CHIP} border-dashed`} onClick={() => setExpanded(true)}>+{hidden} more</button> : null}
            {expanded && aliases.length > CHIP_PREVIEW ? <button type="button" className={`${CHIP} border-dashed`} onClick={() => setExpanded(false)}>Show fewer</button> : null}
        </div>
    );
}

function GroupCard({ group, lookup, onOpenFinding, onOpenEvidence }) {
    const c = group.counts;
    const judged = group.judged;
    const where = [
        plural(c.findings, "finding"),
        `in ${c.sessions} of ${c.sessionsOfSelected} selected Prompt Session${c.sessionsOfSelected === 1 ? "" : "s"}`,
        c.endpointsOfSelected ? `${c.endpoints} of ${c.endpointsOfSelected} selected endpoint${c.endpointsOfSelected === 1 ? "" : "s"}${c.endpointUnknown ? " plus unattributed" : ""}` : "no endpoint attribution",
        `agent types: ${c.agentTypes.join(", ")}`,
        c.evidenceLocations
            ? `${plural(c.evidenceLocations, "evidence location")}${c.evidenceMessages ? ` in ${plural(c.evidenceMessages, "message")}` : ""}, ${plural(c.distinctEvidenceContent, "distinct text")}`
            : "no evidence cited",
    ].join(" - ");
    return (
        <div className="border-t border-[color:var(--line-soft)] py-[8px]">
            <div className="flex flex-wrap items-baseline justify-between gap-[8px]">
                <span className={`${INK} font-medium`}>{group.id}. {group.title || "Untitled group"}</span>
                {judged ? <span className={DOCKET_NOTE_CLASS}>{ATTENTION_LABEL[judged.attention]} - evidence {judged.evidenceStrength}</span> : null}
            </div>
            <div className={DOCKET_NOTE_CLASS}>{where}</div>
            {group.description ? <p className={PROSE}>{group.description}</p> : null}
            {group.distribution ? <p className={PROSE}>Within this selection: {group.distribution}</p> : null}
            {group.repeatedCaptureNote ? <p className={PROSE}>Repeated captures: {group.repeatedCaptureNote}</p> : null}
            {judged ? <>
                {judged.potentialImpact ? <p className={PROSE}>Potential impact: {judged.potentialImpact}</p> : null}
                {judged.benignExplanations ? <p className={PROSE}>Benign explanations: {judged.benignExplanations}</p> : null}
                {judged.contradictions ? <p className={PROSE}>Contradicting evidence: {judged.contradictions}</p> : null}
            </> : null}
            <AliasChips aliases={group.members} lookup={lookup.findings} label="Findings" onOpen={onOpenFinding} render={(f) => f ? `${f.title || "Untitled finding"} - ${f.behaviourSeverity || "unknown"} - report ${f.reportId}` : ""} />
            <AliasChips aliases={group.evidence} lookup={lookup.evidence} label="Evidence" onOpen={onOpenEvidence} render={(e) => e ? `${e.provenance}${e.located ? ", located" : ""}: ${e.text}` : ""} />
        </div>
    );
}

function Result({ result, onOpenFinding, onOpenEvidence, onInvestigate }) {
    const { report, issues, lookup, packet, template, model } = result;
    const cov = report.coverage;
    const grouped = cov.grouped;
    return (
        <div aria-label="Findings review result" className="mt-[10px]">
            <div className={DOCKET_NOTE_CLASS}>
                {TEMPLATES[template.id].label} v{template.version} - {model || "default model"} - reviewed {plural(cov.uniqueFindings, "finding")} across {plural(lookup.sessions.size, "Prompt Session")}
                {cov.duplicateFindingRecords ? ` - ${plural(cov.duplicateFindingRecords, "duplicate record")} collapsed` : ""}
                {` - ${cov.distinctEvidenceContent} distinct evidence texts from ${cov.rawCitations} citations`}
                {cov.resolvedEvidence ? "" : " - evidence is the review's stored quotes, not a fresh read of the conversation"}
                {result.cached ? " - shown from this session's earlier run" : ""}
            </div>
            {!report.complete ? <p className={`${PROSE} text-[color:var(--ink)]`}>{plural(cov.unaddressed, "finding")} were not addressed by the review and are listed as unassessed. Treat this result as incomplete.</p> : null}
            {cov.truncated ? <p className={PROSE}>The selection was cut at {cov.uniqueFindings} findings; {cov.omittedFindings} more were not reviewed. Narrow the time window to review them.</p> : null}
            {cov.unresolvedRefs?.length ? <p className={PROSE}>{plural(cov.unresolvedRefs.length, "selected finding")} could not be read and were not reviewed.</p> : null}
            <p className={`${INK} mt-[8px]`}>{report.summary || "The review returned no summary."}</p>
            {report.conclusion ? <div className="mt-[8px]">
                <div className={SECTION_EYEBROW_CLASS}>Assessment</div>
                <div className={`${INK} mt-[3px] font-medium`}>{CONCLUSION_LABEL[report.conclusion]}</div>
            </div> : null}
            <div className="mt-[10px]">
                <div className={SECTION_EYEBROW_CLASS}>{report.groups.length ? `${plural(report.groups.length, "group")} - ${plural(grouped, "finding")}` : "No groups"}</div>
                {report.groups.map((g) => <GroupCard key={g.id} group={g} lookup={lookup} onOpenFinding={onOpenFinding} onOpenEvidence={onOpenEvidence} />)}
            </div>
            {report.comparisons.length ? <div className="mt-[10px]">
                <div className={SECTION_EYEBROW_CLASS}>Comparisons across the selection</div>
                {report.comparisons.map((c, i) => <div key={i} className="mt-[4px]"><span className={INK}>{c.topic}</span><p className={PROSE}>{c.observation}</p>
                    {c.members.length ? <AliasChips aliases={c.members} lookup={lookup.findings} label="Findings" onOpen={onOpenFinding} render={(f) => f?.title || ""} /> : null}</div>)}
            </div> : null}
            {report.unassessed.length ? <div className="mt-[10px]">
                <div className={SECTION_EYEBROW_CLASS}>{plural(report.unassessed.length, "unassessed finding")}</div>
                {report.unassessed.map((u) => <div key={u.finding} className="mt-[4px] flex flex-wrap items-baseline gap-[6px]">
                    <button type="button" className={CHIP} onClick={() => onOpenFinding?.(lookup.findings.get(u.finding))}>{u.finding}</button>
                    <span className={DOCKET_NOTE_CLASS}>{u.reason}</span>
                </div>)}
            </div> : null}
            {report.gaps.length ? <div className="mt-[10px]">
                <div className={SECTION_EYEBROW_CLASS}>What the evidence cannot answer</div>
                {report.gaps.map((g, i) => <div key={i} className="mt-[6px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-[8px]">
                        <span className={INK}>{g.question}</span>
                        {onInvestigate && gapIsAnswerable(g) ? <button type="button" className={BUTTON} onClick={() => onInvestigate({ gap: g, text: buildGapChatSeed({ gap: g, lookup, selection: packet.selection, coverage: packet.coverage, template }) })}>Investigate in chat</button> : null}
                    </div>
                    {g.evidenceNeeded ? <p className={PROSE}>Needed: {g.evidenceNeeded}</p> : null}
                    {g.source === "external" ? <p className={DOCKET_NOTE_CLASS}>Needs evidence outside Origin's data.</p> : null}
                </div>)}
            </div> : null}
            {issues.length ? <details className="mt-[10px]"><summary className={`${DOCKET_NOTE_CLASS} cursor-pointer`}>{plural(issues.length, "validation note")}</summary>
                <ul className="mt-[4px] list-disc pl-[18px]">{issues.map((s, i) => <li key={i} className={DOCKET_NOTE_CLASS}>{s}</li>)}</ul></details> : null}
        </div>
    );
}

export default function FindingsReviewPanel({
    scopeKey, historyKey = scopeKey, historyAvailable, onRestoreInput = null, loadInput, onOpenFinding = null, onOpenEvidence = null, onInvestigate = null,
    available = true, unavailableReason = "", selectionDescription = "Reviews the selected findings.", runReview = defaultRunReview, maxBytes = DEFAULT_MAX_PACKET_BYTES,
}) {
    const [creds, setCreds] = useState(null);
    useEffect(() => {
        let on = true;
        getChatCreds().then((c) => { if (on) setCreds(c); }).catch(() => { /* unauth/offline */ });
        return () => { on = false; };
    }, []);
    const [templateId, setTemplateId] = useState("overview");
    const [modelChoice, setModelChoice] = useState("");
    const model = modelChoice || defaultReviewModel(creds);
    const modelOptions = useMemo(() => {
        if (!creds?.provider) return [];
        const ranked = getRankedModels(creds.provider).map(({ model: m }) => ({ value: m.id, label: m.contextWindow ? `${m.name} - ${Math.round(m.contextWindow / 1000)}k` : m.name }));
        return ranked.some((o) => o.value === model) || !model ? ranked : [{ value: model, label: model }, ...ranked];
    }, [creds?.provider, model]);

    const [state, setState] = useState({ status: "idle" });
    const history = useSyncExternalStore(subscribeReviewHistory, getReviewHistory);
    const canReadHistory = historyAvailable ?? available;
    const previous = canReadHistory ? history.filter((entry) => entry.historyKey === historyKey) : [];
    useEffect(() => onAuthIdentityChange(() => {
        runIdRef.current++;
        abortRef.current?.abort();
        setState({ status: "idle" });
    }), []);
    useEffect(() => holdAutomaticChunkReload(), []);
    const runIdRef = useRef(0);
    const abortRef = useRef(null);
    // A new scope drops any in-flight run and its result. Re-running is explicit.
    useEffect(() => {
        runIdRef.current++;
        abortRef.current?.abort();
        setState({ status: "idle" });
        return () => abortRef.current?.abort();
    }, [scopeKey]);

    const stop = () => {
        runIdRef.current++;
        abortRef.current?.abort();
        setState({ status: "idle" });
    };

    const run = async (force = false) => {
        const runId = ++runIdRef.current;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const epoch = getAuthIdentityEpoch();
        const fresh = () => runId === runIdRef.current && epoch === getAuthIdentityEpoch();
        setState({ status: "collecting", progress: null });
        let input;
        try {
            input = await loadInput({ signal: controller.signal, onProgress: (p) => { if (fresh()) setState({ status: "collecting", progress: p }); } });
        } catch (e) {
            if (!fresh()) return;
            setState(controller.signal.aborted ? { status: "idle" } : { status: "error", error: e instanceof Error ? e.message : String(e), stage: "collecting" });
            return;
        }
        if (!fresh()) return;
        setState({ status: "running", input, startedAt: Date.now() });
        let r;
        try {
            r = await runReview({ ...input, templateId, model: model || null, maxBytes, force, signal: controller.signal });
        } catch (e) {
            r = { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
        }
        if (!fresh()) return;
        if (r.status === "done") {
            rememberReview({ historyKey, scopeKey, input, result: r, selectionDescription, epoch });
            setState({ status: "done", result: r, input });
        }
        else if (r.status === "too_large") setState({ status: "too_large", result: r, input });
        else if (r.status === "aborted") setState({ status: "idle" });
        else setState({ status: "error", error: r.error?.message || "The review did not complete.", stage: "running" });
    };

    // Elapsed seconds while the model runs, so the status line visibly moves.
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (state.status !== "running") return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [state.status]);
    const elapsed = state.status === "running" ? Math.max(0, Math.round((now - state.startedAt) / 1000)) : 0;

    const busy = state.status === "collecting" || state.status === "running";
    const noProvider = creds && !creds.provider;
    const notes = state.input?.notes;
    const moduleLoadFailed = state.status === "error" && isModuleLoadError(state.error);
    return (
        <div aria-label="Findings review" aria-busy={busy} className="min-w-0">
            {previous.length > 0 && <section aria-label="Previous findings reviews" className="mb-[12px] border border-[color:var(--line)] p-[10px]">
                <div className={SECTION_EYEBROW_CLASS}>Previous reviews ({previous.length})</div>
                <p className={PROSE}>Open a completed review without running the model again. Available in this page session only; reloading clears this history.</p>
                <div className="max-h-[220px] overflow-auto">{previous.map((entry) => <div key={entry.id} className="flex flex-wrap items-center justify-between gap-[8px] border-t border-[color:var(--line)] py-[8px]">
                    <div className="min-w-0"><div className={INK}>{TEMPLATES[entry.result.template.id].label} / {plural(entry.result.packet.findings, "finding")} / {entry.result.model || "Default model"}</div>
                        <time className={DOCKET_NOTE_CLASS} dateTime={new Date(entry.completedAt).toISOString()}>{new Date(entry.completedAt).toLocaleString()}</time>
                        <p className={PROSE}>{entry.selectionDescription}{entry.scopeKey !== scopeKey ? " Earlier selection or investigation revision." : ""}</p></div>
                    <button type="button" className={BUTTON} disabled={busy} onClick={() => {
                        onRestoreInput?.(entry.input);
                        setState({ status: "done", result: { ...entry.result, cached: true }, input: entry.input, previous: entry });
                    }}>Open review</button>
                </div>)}</div>
            </section>}
            {!available && <p className={PROSE}>{unavailableReason}</p>}
            {(available || (canReadHistory && state.previous)) && <>
                <div className="flex flex-wrap items-center gap-[8px]">
                    <Select value={templateId} onChange={(v) => setTemplateId(String(v))} disabled={busy}
                        options={TEMPLATE_IDS.map((id) => ({ value: id, label: TEMPLATES[id].label }))}
                        ariaLabel="Review template" triggerStyle={SELECT_STYLE} className="px-[7px]" />
                    {modelOptions.length ? <Select value={model} onChange={(v) => setModelChoice(String(v))} disabled={busy} options={modelOptions}
                        ariaLabel="Model for the findings review" triggerStyle={SELECT_STYLE} className="px-[7px]" /> : null}
                    {busy
                        ? <button type="button" className={BUTTON} onClick={stop}>Stop</button>
                        : <button type="button" className={BUTTON} disabled={!available || !!noProvider} onClick={() => run(state.status === "done")}>{state.status === "done" ? "Run again" : "Review findings"}</button>}
                    {noProvider ? <span className={DOCKET_NOTE_CLASS}>Configure a model provider to run a review.</span> : null}
                </div>
                <p className={PROSE}>
                    {selectionDescription}{" "}
                    Completed reviews stay available in this page session; they are not saved to the investigation. Reloading clears them. Leaving this investigation cancels a running review.
                </p>
                {state.status === "collecting" ? <p className={PROSE} role="status">
                    {state.progress?.phase === "details" ? `Reading stored evidence for ${state.progress.count} of ${state.progress.total} findings...`
                        : state.progress?.phase === "members" ? `Collecting findings... ${state.progress.count} so far` : "Collecting findings..."}
                </p> : null}
                {state.status === "running" ? <p className={PROSE} role="status">
                    Reviewing {plural(state.input.findings.length, "finding")} with {model || "the default model"}. Waiting for the model, {elapsed}s...
                </p> : null}
                {notes && (notes.membersTruncated || notes.detailUnavailable || notes.attributionUnavailable) && state.status !== "idle" ? <p className={PROSE}>
                    {notes.membersTruncated ? `Only the first ${notes.members} findings were collected. ` : ""}
                    {notes.detailUnavailable ? `${plural(notes.detailUnavailable, "finding")} had no readable detail and will be marked unassessed. ` : ""}
                    {notes.attributionUnavailable ? "Some AI agent type information was unavailable." : ""}
                </p> : null}
                {state.status === "too_large" ? <p className={PROSE} role="status">
                    {plural(state.result.packet.findings, "finding")} render to {kb(state.result.rendered.bytes)}, above the {kb(state.result.rendered.maxBytes)} review limit. Choose fewer findings and run again. Nothing was sent to the model.
                </p> : null}
                {state.status === "error" ? <div>
                    <p className={PROSE} role="alert">{state.stage === "collecting" ? "Findings did not load: " : "The review did not complete: "}{moduleLoadFailed ? "A dashboard code file could not be loaded. Reload the dashboard, then run the review again." : state.error}</p>
                    {moduleLoadFailed ? <button type="button" className={BUTTON} onClick={() => window.location.reload()}>Reload dashboard</button> : <button type="button" className={BUTTON} onClick={() => run(false)}>Retry</button>}
                </div> : null}
                {state.previous && <p role="status" className={PROSE}>Viewing the review completed {new Date(state.previous.completedAt).toLocaleString()}.{state.previous.scopeKey !== scopeKey ? " This result covers an earlier selection or investigation revision, not the current selection." : ""} Running again uses the current selection.</p>}
                {state.status === "done" && (available || canReadHistory) ? <Result result={state.result} onOpenFinding={onOpenFinding} onOpenEvidence={onOpenEvidence} onInvestigate={onInvestigate} /> : null}
            </>}
        </div>
    );
}
